import React from 'react';
import { Avatar, Space, Tooltip, Button } from 'antd';
import InvoicePreviewWithIcon from '../../NetSuiteJournalEntriesPage/InvoicePreviewWithIcon';
import { CheckIcon, CloseIcon } from '../../../../icons';
import { accountingHelpers, dateHelpers, expensesHelpers, textHelpers } from '../../../../utils/helpers';
import { StyledExpensesTabActionsSpace, StyledExpensesTabIcon } from './StyledExpensesTab';

const dateColumnProps = accountingHelpers.getDateColumnProps();
const summaraizeKeyName = 'isMonthGrouping';
const columnProps = { width: '20%' };

export function getExpensesTabColumns({ t, isAdmin, employeeEmail, onApprove, onReject }) {
  const getAvatar = ({ user, key, ...rest }) => {
    if (!user) return null;
    const name = user?.full_name || user?.email;
    return (
      <div key={key} {...rest}>
        <Tooltip title={name}>
          <Avatar src={user?.logo} size={24} style={{fontSize: 12}}>
            {textHelpers.getInitials(name)}
          </Avatar>
        </Tooltip>
      </div>
    );
  };

  const handleAction = ({ action, e, expense }) => {
    const override = isAdmin && !expensesHelpers.isExpenseAlreadyApprovedByEmployee(expense, employeeEmail);
    action?.({ e, expense, data: { admin_override: override } });
  };

  const getActions = (expense) => {
    if (expense?.[summaraizeKeyName]) return;
    const isExpenseInReview = expensesHelpers.isExpenseInReview(expense);
    const isEmployeeInApprovalList = expensesHelpers.isEmployeeInExpenseApprovalList(expense, employeeEmail);
    const isAlreadyApproved = expensesHelpers.isExpenseAlreadyApprovedByEmployee(expense, employeeEmail);
    const isAnyAdminApproval = expensesHelpers.isAnyAdminApproval(expense);
    const invoiceId = expense?.company_invoice_id;
    const canTakeAction = isExpenseInReview && (
      (isAdmin && isAnyAdminApproval) ||
      ((isAdmin || isEmployeeInApprovalList) && !isAlreadyApproved)
    );
    const icon = expensesHelpers.getExpenseInvoiceMatchType(expense);
    return (
      <StyledExpensesTabActionsSpace size='middle' align='center'>
        <StyledExpensesTabIcon>
          {(invoiceId && icon) ? <InvoicePreviewWithIcon invoiceId={invoiceId} icon={icon} /> : icon}
        </StyledExpensesTabIcon>
        {canTakeAction && (
          <Space direction='horizontal'>
            <Tooltip title={t('reject')}>
              <Button size='small' icon={<CloseIcon />} onClick={(e) => handleAction({ action: onReject, e, expense })} />
            </Tooltip>
            <Tooltip title={t('approve')}>
              <Button size='small' icon={<CheckIcon />} onClick={(e) => handleAction({ action: onApprove, e, expense })} />
            </Tooltip>
          </Space>
        )}
      </StyledExpensesTabActionsSpace>
    );
  };

  return [
    {
      ...dateColumnProps,
      ...columnProps,
      dataIndex: 'date',
      render: (_, record) => dateHelpers.getDateFromISO(record?.created_at, record?.[summaraizeKeyName] ? 'MMM YYYY' : 'DD MMM'),
      title: t('date'),
    },
    {
      dataIndex: 'payment_type',
      title: `${t('payment')} ${t('method')}`,
      render: (_, record) => {
        if (record?.[summaraizeKeyName]) return;
        return expensesHelpers.getExpensePaymentMethodIcon(record, t);
      }
    },
    {
      ...columnProps,
      dataIndex: 'expense_type',
      render: (_, record) => {
        if (record?.[summaraizeKeyName]) return;
        return expensesHelpers.getExpenseTypeInfo(record, t);
      },
      title: t('type'),
    },
    {
      ...columnProps,
      dataIndex: 'reported_by',
      render: (user, record) => {
        if (record?.[summaraizeKeyName]) return;
        return getAvatar({ user });
      },
      title: t('user'),
    },
    {
      ...columnProps,
      dataIndex: 'approval',
      render: (_, record) => {
        if (record?.[summaraizeKeyName]) return;
        const approver = expensesHelpers.getExpenseApprovals(record);
        if (approver.length === 0 && expensesHelpers.isAnyAdminApproval(record)) {
          return `${t('any')} ${t('admin')}`;
        }
        const adminOverride = expensesHelpers.getAdminOverrideApproval(record);
        if (adminOverride) {
          return getAvatar({ user: adminOverride.approver });
        }
        return (
          <Avatar.Group>
            {approver.map(({ approver }, key) => getAvatar({ user: approver, key }))}
          </Avatar.Group>
        );
      },
      title: t('approver'),
    },
    {
      ...columnProps,
      align: 'right',
      className: 'ghost-table-cell',
      render: (_, record) => expensesHelpers.getExpenseStatusLabel(record, t),
    },
    {
      ...columnProps,
      align: 'right',
      dataIndex: 'price',
      render: (_, record) => (
        <div className='amount'>
          {expensesHelpers.getExpensePrice(record)}
        </div>
      ),
      title: t('amount'),
    },
    {
      align: 'right',
      dataIndex: 'match',
      render: (_, record) => getActions(record),
      title: ' '
    }
  ];
}

